/**
 * Prefetch en background de los chunks lazy de cada área.
 *
 * Uso: cada layout llama a `prefetchArea("iipp")` dentro de un useEffect.
 * Cuando el usuario navega luego a una página interna del área, el chunk
 * ya está descargado y React monta sin pasar por el Suspense fallback
 * (sin "flash" de spinner).
 *
 * IMPORTANTE: las rutas `import(...)` deben coincidir EXACTAMENTE con las
 * usadas en src/App.tsx dentro de `lazyWithRetry(...)`. Vite cachea por URL
 * del módulo, así prefetch y lazy reusan el mismo chunk.
 */

type Loader = () => Promise<unknown>;

const loadersByArea: Record<string, Loader[]> = {
  iipp: [
    () => import("@/pages/app/oposiciones-iipp/OposicionesIIPPDashboard"),
    () => import("@/pages/app/oposiciones-iipp/TestExamIIPPPage"),
    () => import("@/pages/app/oposiciones-iipp/CasePracticeIIPPPage"),
    () => import("@/pages/app/oposiciones-iipp/FlashcardsIIPPPage"),
    () => import("@/pages/app/oposiciones-iipp/ResourcesIIPPPage"),
    () => import("@/pages/app/oposiciones-iipp/ProgressIIPPPage"),
  ],
  c1: [
    () => import("@/pages/app/oposiciones-c1/OposicionesC1Dashboard"),
    () => import("@/pages/app/oposiciones-c1/TestExamC1Page"),
    () => import("@/pages/app/oposiciones-c1/CasePracticeC1Page"),
    () => import("@/pages/app/oposiciones-c1/FlashcardsC1Page"),
    () => import("@/pages/app/oposiciones-c1/ResourcesC1Page"),
    () => import("@/pages/app/oposiciones-c1/ProgressC1Page"),
  ],
  c2: [
    () => import("@/pages/app/oposiciones-c2/OposicionesC2Dashboard"),
    () => import("@/pages/app/oposiciones-c2/TestExamC2Page"),
    () => import("@/pages/app/oposiciones-c2/CasePracticeC2Page"),
    () => import("@/pages/app/oposiciones-c2/FlashcardsC2Page"),
    () => import("@/pages/app/oposiciones-c2/ResourcesC2Page"),
    () => import("@/pages/app/oposiciones-c2/ProgressC2Page"),
  ],
  gc: [
    () => import("@/pages/app/guardia-civil/OposicionesGCDashboard"),
    () => import("@/pages/app/guardia-civil/TestExamGCPage"),
    () => import("@/pages/app/guardia-civil/ExamenOficialGCPage"),
    () => import("@/pages/app/guardia-civil/FlashcardsGCPage"),
    () => import("@/pages/app/guardia-civil/ResourcesGCPage"),
    () => import("@/pages/app/guardia-civil/ProgressGCPage"),
    () => import("@/pages/app/guardia-civil/EnglishB1Page"),
    () => import("@/pages/app/guardia-civil/LenguaPage"),
    () => import("@/pages/app/guardia-civil/PsicotecnicoPage"),
  ],
  oposiciones: [
    () => import("@/pages/app/oposiciones/OposicionesDashboard"),
    () => import("@/pages/app/oposiciones/TestExamPage"),
    () => import("@/pages/app/oposiciones/CasePracticePage"),
    () => import("@/pages/app/oposiciones/FlashcardsPage"),
    () => import("@/pages/app/oposiciones/ResourcesPage"),
    () => import("@/pages/app/oposiciones/ProgressPage"),
  ],
  admin: [
    () => import("@/pages/admin/AdminDashboardPage"),
    () => import("@/pages/admin/AdminUsersPage"),
    () => import("@/pages/admin/AdminUsagePage"),
    () => import("@/pages/admin/AdminAgentsPage"),
    () => import("@/pages/admin/AdminBillingPage"),
    () => import("@/pages/admin/AdminSecurityPage"),
    () => import("@/pages/admin/AdminMessagesPage"),
    () => import("@/pages/admin/AdminConsultasPage"),
    () => import("@/pages/admin/AdminDocumentModelsPage"),
    () => import("@/pages/admin/AdminExportPage"),
    () => import("@/pages/admin/AdminOposicionesPage"),
    () => import("@/pages/admin/AdminGCVisualsPage"),
    () => import("@/pages/admin/AdminContenidoOposicionesPage"),
    () => import("@/pages/admin/AdminPlansSettingsPage"),
    () => import("@/pages/admin/AdminErrorsPage"),
  ],
};

// Marcamos áreas ya prefetchadas en esta sesión para no relanzarlas en cada
// remount del layout (p.ej. al navegar entre rutas internas).
const prefetched = new Set<string>();

/**
 * Lanza en background (idle) la descarga de los chunks de un área.
 * Idempotente: solo se ejecuta una vez por sesión y por área.
 * Tolera fallos en silencio: el `lazyWithRetry` normal cubrirá el caso
 * si el usuario navega antes de que termine el prefetch.
 */
export function prefetchArea(area: keyof typeof loadersByArea | string): void {
  if (typeof window === "undefined") return;
  if (prefetched.has(area)) return;
  const loaders = loadersByArea[area];
  if (!loaders || loaders.length === 0) return;
  prefetched.add(area);

  const run = () => {
    for (const loader of loaders) {
      // Disparamos en paralelo, sin await; los errores no deben propagarse.
      loader().catch(() => {
        /* silencioso: el lazy normal lo reintentará si hace falta */
      });
    }
  };

  // requestIdleCallback cuando exista; fallback a setTimeout corto.
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout?: number }) => number)
    | undefined;
  if (typeof ric === "function") {
    ric(run, { timeout: 1500 });
  } else {
    setTimeout(run, 300);
  }
}
